import { Badge } from '@/components';
import { LoadButton, NoDataMessage, isNoData } from '../shared';
import { SalesHistoryIcon } from '../icons';
import type { UseQueryResult } from '@tanstack/react-query';

interface SalesHistoryTabProps {
  query: UseQueryResult<any>;
  loadEnabled: boolean;
  onLoad: () => void;
  disabled?: boolean;
}

export function SalesHistoryTab({ query, loadEnabled, onLoad, disabled }: SalesHistoryTabProps) {
  return (
    <div>
      {!loadEnabled && (
        <div className="flex flex-col items-center justify-center py-12">
          <SalesHistoryIcon className="w-12 h-12 opacity-40 mb-4" />
          <p className="text-gray-400 text-sm mb-4">View past sales records with prices, locations, and conditions</p>
          <LoadButton label="Load Sales History" onClick={onLoad} isLoading={false} isLoaded={false} disabled={disabled} />
        </div>
      )}

      {query.isLoading && (
        <p className="text-gray-500 text-sm text-center py-8">Loading sales history...</p>
      )}

      {query.error && !isNoData(query) && (
        <p className="text-red-400 text-sm text-center py-4">Error loading sales history</p>
      )}

      {isNoData(query) && (
        <NoDataMessage message="No sales records found for this vehicle" />
      )}

      {query.data?.data && Array.isArray(query.data.data) && !isNoData(query) && (
        <div className="space-y-3">
          {query.data.data.length > 0 ? (
            query.data.data.map((sale: any, idx: number) => (
              <div key={idx} className="bg-obdai-deep p-4 rounded-xl">
                <div className="flex justify-between items-start">
                  <div>
                    {sale.sale_date && <p className="text-gray-400 text-sm">{sale.sale_date}</p>}
                    {sale.price && <p className="text-green-400 font-bold text-xl">{sale.price}</p>}
                  </div>
                  {sale.odometer && <Badge variant="default">{sale.odometer}</Badge>}
                </div>
                {sale.location && <p className="text-gray-400 text-sm mt-2">Location: {sale.location}</p>}
                {sale.condition && <p className="text-gray-400 text-sm">Condition: {sale.condition}</p>}
              </div>
            ))
          ) : (
            <NoDataMessage message="No sales history found" />
          )}
        </div>
      )}
    </div>
  );
}
